// ─────────────────────────────────────────────────────────────────────────────
//  SelectionController
//
//  Encapsulates all row-selection state and logic for uwc-datatable.
//
//  Responsibilities:
//    • Track which rows are selected (_selectionKeys set).
//    • Handle single, multiple, range (shift-click), and meta-key selection.
//    • Handle select-all / deselect-all.
//    • Fire host-level 'uwc-row-select' and 'uwc-row-unselect' events.
//    • Build the selection output value (single row or array) for external binding.
//    • Sync state from the host's `selection` property.
//
//  The host owns 'uwc-selection-change' dispatch (it needs `this.value` to build
//  the full output); this controller fires only row-level events.
//
//  Usage inside UwcDatatable:
//
//    private _selection = new SelectionController(
//      this,
//      (row) => this._getRowKey(row),   // closure — reads dataKey at call time
//    );
//
//    // In willUpdate:
//    if (changed.has('selectionMode'))    this._selection.selectionMode    = this.selectionMode;
//    if (changed.has('metaKeySelection')) this._selection.metaKeySelection = this.metaKeySelection;
//    if (changed.has('selection') || changed.has('dataKey')) {
//      this._selection.syncFromProp(this.selection);
//    }
//
//    // In render:
//    const selected = this._selection.isSelected(row);
//    const isAll    = allRows.every(r => this._selection.isSelected(r));
//    const isSome   = allRows.some(r => this._selection.isSelected(r)) && !isAll;
//
//    // On row click:
//    this._selection.handleClick(row, absIdx, event, allSortedFilteredRows);
//    this._emitSelectionChange();
//
//    // On select-all toggle:
//    this._selection.toggleAll(allRows, isAll);
//    this._emitSelectionChange();
//
//    // Build selection output for uwc-selection-change:
//    const output = this._selection.buildOutput(this.value ?? []);
//
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactiveController, ReactiveControllerHost } from 'lit';

export type DataTableSelectionMode = 'single' | 'multiple';

export class SelectionController implements ReactiveController {
  private readonly _host: ReactiveControllerHost & EventTarget;
  private _getKey: (row: Record<string, unknown>) => unknown;

  private _selectionKeys  = new Set<unknown>();
  private _lastClickedIdx?: number;

  /** Mirrors the host's selectionMode property. */
  selectionMode?: DataTableSelectionMode;

  /** Mirrors the host's metaKeySelection property. */
  metaKeySelection?: boolean;

  constructor(
    host: ReactiveControllerHost & EventTarget,
    getKey: (row: Record<string, unknown>) => unknown,
  ) {
    this._host   = host;
    this._getKey = getKey;
    host.addController(this);
  }

  hostConnected()    {}
  hostDisconnected() {}

  // ── Public API ─────────────────────────────────────────────────────────────

  /** True when a row is in the current selection. */
  isSelected(row: Record<string, unknown>): boolean {
    return this._selectionKeys.has(this._getKey(row));
  }

  /**
   * Handle a row click or keyboard activation.
   * Mutates internal selection state and fires uwc-row-select / uwc-row-unselect.
   * The host is responsible for emitting uwc-selection-change afterward.
   *
   * @param allSortedFilteredRows - Full visible dataset used for range (shift-click) calculation.
   */
  handleClick(
    row: Record<string, unknown>,
    absIdx: number,
    e: MouseEvent | KeyboardEvent,
    allSortedFilteredRows: Record<string, unknown>[],
  ): void {
    if (!this.selectionMode) return;

    const isKeyboard    = e instanceof KeyboardEvent;
    const isTouchDevice = navigator.maxTouchPoints > 0;
    const metaActive    = !isKeyboard && !isTouchDevice && ((e as MouseEvent).metaKey || (e as MouseEvent).ctrlKey);
    const shiftActive   = !isKeyboard && !isTouchDevice && (e as MouseEvent).shiftKey;
    const key           = this._getKey(row);
    const isSelected    = this._selectionKeys.has(key);
    // Default: single→true (meta needed to unselect), multiple→false (no meta needed)
    const useMeta       = this.metaKeySelection ?? (this.selectionMode === 'single');

    if (this.selectionMode === 'single') {
      const canDeselect = isKeyboard || !useMeta || metaActive;
      if (isSelected && canDeselect) {
        this._selectionKeys = new Set();
        this._emit('uwc-row-unselect', row, absIdx, e);
      } else if (!isSelected) {
        this._selectionKeys = new Set([key]);
        this._emit('uwc-row-select', row, absIdx, e);
      }
    } else {
      // multiple
      if (!isKeyboard && shiftActive && this._lastClickedIdx !== undefined) {
        // Range selection: select all rows between last click and current
        const from = Math.min(this._lastClickedIdx, absIdx);
        const to   = Math.max(this._lastClickedIdx, absIdx);
        const next = new Set(this._selectionKeys);
        allSortedFilteredRows.slice(from, to + 1).forEach(r => next.add(this._getKey(r)));
        this._selectionKeys = next;
      } else if (!isKeyboard && useMeta && !metaActive) {
        // metaKeySelection=true but no meta held: replace entire selection
        const wasSelected = isSelected;
        this._selectionKeys = new Set([key]);
        if (!wasSelected) this._emit('uwc-row-select', row, absIdx, e);
      } else {
        // Toggle (keyboard always toggles; metaKeySelection=false; meta held when metaKeySelection=true)
        const next = new Set(this._selectionKeys);
        if (isSelected) {
          next.delete(key);
          this._emit('uwc-row-unselect', row, absIdx, e);
        } else {
          next.add(key);
          this._emit('uwc-row-select', row, absIdx, e);
        }
        this._selectionKeys = next;
      }
    }

    this._lastClickedIdx = absIdx;
    this._host.requestUpdate();
  }

  /**
   * Toggle select-all / deselect-all over the given rows.
   * The host is responsible for emitting uwc-selection-change afterward.
   */
  toggleAll(allRows: Record<string, unknown>[], allSelected: boolean): void {
    if (allSelected) {
      this._selectionKeys = new Set();
    } else {
      const next = new Set(this._selectionKeys);
      allRows.forEach(r => next.add(this._getKey(r)));
      this._selectionKeys = next;
    }
    this._host.requestUpdate();
  }

  /**
   * Build the selection output suitable for uwc-selection-change detail.
   * Filters `allData` (the full value array) so deselected rows are excluded
   * even if they were removed from the visible page.
   */
  buildOutput(
    allData: Record<string, unknown>[],
  ): Record<string, unknown> | Record<string, unknown>[] | null {
    const selected = allData.filter(r => this._selectionKeys.has(this._getKey(r)));
    if (this.selectionMode === 'single') return selected[0] ?? null;
    return selected;
  }

  /** Sync internal selection-keys state from the host's `selection` property. */
  syncFromProp(
    selection: Record<string, unknown> | Record<string, unknown>[] | null | undefined,
  ): void {
    this._selectionKeys = new Set();
    if (!selection) return;
    const items = Array.isArray(selection) ? selection : [selection];
    for (const row of items) {
      this._selectionKeys.add(this._getKey(row));
    }
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private _emit(
    eventName: 'uwc-row-select' | 'uwc-row-unselect',
    row: Record<string, unknown>,
    index: number,
    originalEvent: Event,
  ): void {
    this._host.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true, composed: true,
      detail: { data: row, index, originalEvent },
    }));
  }
}
